import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

const TaxCalculation = () => {
  const { t } = useTranslation();
  
  // Netto Calculation (Left Side)
  const [inputAmount, setInputAmount] = useState('');
  const [vatRate, setVatRate] = useState('');
  const [netAmount, setNetAmount] = useState('');
  const [vatAmount, setVatAmount] = useState('');

  // VAT Calculation (Right Side)
  const [netPrice, setNetPrice] = useState('');
  const [vatRateRight, setVatRateRight] = useState('');
  const [calculatedVatAmount, setCalculatedVatAmount] = useState('');
  const [grossAmount, setGrossAmount] = useState('');

  // Calculate netto (left side) whenever inputAmount or vatRate changes
  useEffect(() => {
    const amount = parseFloat(inputAmount) || 0;
    const rate = parseFloat(vatRate) || 0;
    
    if (amount > 0 && rate > 0) {
      // Correct formula: Input Amount / (1 + (VAT Rate / 100))
      const calculatedNetAmount = amount / (1 + (rate / 100));
      const calculatedVatAmount = calculatedNetAmount * (rate / 100);
      
      setNetAmount(calculatedNetAmount.toFixed(2));
      setVatAmount(calculatedVatAmount.toFixed(2));
    } else {
      setNetAmount('');
      setVatAmount('');
    }
  }, [inputAmount, vatRate]);

  // Calculate VAT (right side) whenever netPrice or vatRateRight changes
  useEffect(() => {
    const price = parseFloat(netPrice) || 0;
    const rate = parseFloat(vatRateRight) || 0;
    
    if (price > 0 && rate > 0) {
      const calculatedVat = price * (rate / 100);
      const calculatedGross = price + calculatedVat;
      
      setCalculatedVatAmount(calculatedVat.toFixed(2));
      setGrossAmount(calculatedGross.toFixed(2));
    } else {
      setCalculatedVatAmount('');
      setGrossAmount('');
    }
  }, [netPrice, vatRateRight]);

  const verificationAmount = parseFloat(netAmount) + parseFloat(vatAmount);

  return (
    <div className="p-6">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">
          {t('taxCalculation.title')}
        </h1>
        <p className="text-gray-600">
          {t('taxCalculation.description')}
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left Side - Netto Calculation */}
        <div className="card">
          <div className="mb-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-2">
              {t('taxCalculation.nettoCalculation')}
            </h2>
            <p className="text-sm text-gray-600">
              {t('taxCalculation.nettoDescription')}
            </p>
          </div>

          <div className="space-y-4">
            {/* Input Amount */}
            <div>
              <label className="label">
                {t('taxCalculation.inputAmount')} *
              </label>
              <input
                type="number"
                step="0.01"
                placeholder="0.00"
                className="input"
                value={inputAmount}
                onChange={(e) => {
                  setInputAmount(e.target.value);
                }}
              />
            </div>

            {/* VAT Rate */}
            <div>
              <label className="label">
                {t('taxCalculation.vatRate')} (%) *
              </label>
              <input
                type="number"
                step="0.01"
                placeholder="19"
                className="input"
                value={vatRate}
                onChange={(e) => {
                  setVatRate(e.target.value);
                }}
              />
            </div>

            {/* Net Amount (Calculated) */}
            <div>
              <label className="label">
                {t('taxCalculation.netAmount')}
              </label>
              <input
                type="text"
                readOnly
                className="input bg-gray-100"
                value={netAmount}
                placeholder={t('taxCalculation.calculatedAutomatically')}
              />
            </div>

            {/* VAT Amount (Calculated) */}
            <div>
              <label className="label">
                {t('taxCalculation.vatAmount')}
              </label>
              <input
                type="text"
                readOnly
                className="input bg-gray-100"
                value={vatAmount}
                placeholder={t('taxCalculation.calculatedAutomatically')}
              />
            </div>

            {/* Verification */}
            <div>
              <label className="label">
                {t('taxCalculation.verification')}
              </label>
              <input
                type="text"
                readOnly
                className={`input ${verificationAmount.toFixed(2) === inputAmount ? 'bg-green-100' : 'bg-yellow-100'}`}
                value={verificationAmount.toFixed(2)}
                placeholder={t('taxCalculation.shouldEqualInput')}
              />
              {verificationAmount.toFixed(2) === inputAmount && (
                <p className="text-green-600 text-sm mt-1">
                  ✅ {t('taxCalculation.calculationCorrect')}
                </p>
              )}
            </div>
          </div>

          {/* Formula Display */}
          <div className="mt-6 p-4 bg-blue-50 rounded-lg">
            <h3 className="font-medium text-blue-900 mb-2">
              {t('taxCalculation.formula')}
            </h3>
            <p className="text-blue-800 text-sm">
              {t('taxCalculation.formulaDescription')}
            </p>
            <code className="block mt-2 text-blue-900 bg-blue-100 p-2 rounded text-sm">
              {t('taxCalculation.formulaCode')}
            </code>
          </div>
        </div>

        {/* Right Side - VAT Calculation */}
        <div className="card">
          <div className="mb-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-2">
              {t('taxCalculation.vatCalculation')}
            </h2>
            <p className="text-sm text-gray-600">
              {t('taxCalculation.vatDescription')}
            </p>
          </div>

          <div className="space-y-4">
            {/* Net Price */}
            <div>
              <label className="label">
                {t('taxCalculation.netPrice')} *
              </label>
              <input
                type="number"
                step="0.01"
                placeholder="0.00"
                className="input"
                value={netPrice}
                onChange={(e) => {
                  setNetPrice(e.target.value);
                }}
              />
            </div>

            {/* VAT Rate */}
            <div>
              <label className="label">
                {t('taxCalculation.vatRate')} (%) *
              </label>
              <input
                type="number"
                step="0.01"
                placeholder="19"
                className="input"
                value={vatRateRight}
                onChange={(e) => {
                  setVatRateRight(e.target.value);
                }}
              />
            </div>

            {/* VAT Amount (Calculated) */}
            <div>
              <label className="label">
                {t('taxCalculation.vatAmount')}
              </label>
              <input
                type="text"
                readOnly
                className="input bg-gray-100"
                value={calculatedVatAmount}
                placeholder={t('taxCalculation.calculatedAutomatically')}
              />
            </div>

            {/* Gross Amount (Calculated) */}
            <div>
              <label className="label">
                {t('taxCalculation.grossAmount')}
              </label>
              <input
                type="text"
                readOnly
                className="input bg-gray-100"
                value={grossAmount}
                placeholder={t('taxCalculation.calculatedAutomatically')}
              />
            </div>
          </div>

          {/* Formula Display */}
          <div className="mt-6 p-4 bg-green-50 rounded-lg">
            <h3 className="font-medium text-green-900 mb-2">
              {t('taxCalculation.vatFormula')}
            </h3>
            <p className="text-green-800 text-sm">
              {t('taxCalculation.vatFormulaDescription')}
            </p>
            <code className="block mt-2 text-green-900 bg-green-100 p-2 rounded text-sm">
              {t('taxCalculation.vatFormulaCode')}
            </code>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TaxCalculation;
